<script lang="ts">
import { defineComponent, inject, onMounted, Ref, ref } from "vue";
import formValidierung from "@/businesslogic/formValidierung";
import AnfrageService from "@/businesslogic/services/AnfrageService";
import Fehlermeldung from "@/views/phv/modals/Fehlermeldung.vue";
import EmailBestaetigung from "@/components/vorschlag/modals/EmailBestaetigung.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import IconSprite from "@/components/IconSprite.vue";
import Logger from "js-logger";
import FerienhausAnfrageService from "@/businesslogic/services/FerienhausAnfrageService";
import storeUtils from "@/businesslogic/utils/storeUtils";
import Anrede from "@/components/Anrede.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export class Empfaenger {
  anrede: string;
  vorname: string;
  nachname: string;
  telefon: string;
  email: string;
  werbeeinwilligung: string;
  datenschutzhinweiseAkzeptiert: boolean;
}

export default defineComponent({
  name: "VorschlagDirekt",
  components: {
    Zeile,
    Zelle,
    TitelZelle,
    Anrede,
    ValidierungsFehler,
    EmailBestaetigung,
    Fehlermeldung,
    IconSprite,
  },
  props: {
    produkt: String,
    isVorschlagEmail: {
      type: Boolean,
      default: true,
    },
    antrag: {
      type: Boolean,
      default: false,
    },
    trackingZusatz: String,
    istOutboundAktiv: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["closeModal", "textVerbergen"],
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const zeigeSpinner: any = inject("zeigeSpinner");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const headerText = ref("");
    const fehler = ref({} as any);
    const showError = ref(false);
    const showSuccess = ref(false);

    const { istFhkVerkaufsprodukt } = storeUtils();

    const state: Ref<Empfaenger> = ref(new Empfaenger());

    function eingabenSindValide() {
      return new Promise((resolve) => {
        const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();
        validiereForm("vorschlagPerMailDirekt").then(() => {
          fehler.value = getFormFehler();
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    }

    function closeModal() {
      emit("closeModal");
    }

    function speicheDatenImStore(empfaenger: Empfaenger) {
      piniaStore.state.personendaten.versicherungsnehmer.anrede = empfaenger.anrede;
      piniaStore.state.personendaten.versicherungsnehmer.vorname = empfaenger.vorname;
      piniaStore.state.personendaten.versicherungsnehmer.nachname = empfaenger.nachname;
      piniaStore.state.personendaten.versicherungsnehmer.email = empfaenger.email;
      piniaStore.state.personendaten.versicherungsnehmer.telefon = empfaenger.telefon;
      piniaStore.state.werbeeinwilligung = empfaenger.werbeeinwilligung;
      piniaStore.state.datenschutzhinweiseAkzeptiert = empfaenger.datenschutzhinweiseAkzeptiert;
    }

    function submitModalData() {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          speicheDatenImStore(state.value);
          if (props.isVorschlagEmail) {
            zeigeSpinner.value = true;
            if (istFhkVerkaufsprodukt(piniaStore.state)) {
              versendeBuendelEmail();
            } else {
              versendeEmail();
            }
          } else if (istFhkVerkaufsprodukt(piniaStore.state)) {
            wechsleSichtbarkeit("modal-ferienhausDownload");
          } else {
            zeigeSpinner.value = true;
            vorschlagHerunterladen();
          }
        } else {
          const elem = document.querySelector("div#tariffOptionsTable");
          if (elem) {
            const rect = elem.getBoundingClientRect(),
              scrollTop = window.scrollY || document.documentElement.scrollTop;
            window.scrollTo(0, rect.top + scrollTop);
          }
        }
      });
    }

    const vorschlagHerunterladen = () => {
      AnfrageService.herunterladen(
        piniaStore.getSessionToken(piniaStore.state),
        piniaStore.state,
        props.antrag,
        "VorschlagDirekt",
        props.trackingZusatz,
      )
        .then((res) => {
          Logger.debug(
            "vorschlagHerunterladen(): versenden erfolgreich. AnfrageID: " + res.data.id,
            props.trackingZusatz,
          );
          piniaStore.state.id = res.data.id;
          piniaStore.state.status = res.data.status;
          piniaStore.state.erstelldatum = res.data.erstelldatum;
          Logger.debug("Response", res.data);
          const fileLink = document.createElement("a");
          fileLink.href = "data:application/pdf;base64," + res.data.korrespondenz.vorschlagDokument;
          fileLink.setAttribute("download", res.data.korrespondenz.dokumentName);
          document.body.appendChild(fileLink);
          fileLink.click();
          piniaStore.state.adresseFelderSchrittDreiDeaktivieren = true;
          emit("closeModal");
        })
        .catch((err) => {
          Logger.debug("FEHLER in vorschlagHerunterladen():", err);
          showError.value = true;
          emit("textVerbergen");
        })
        .then(() => {
          //wird immer ausgeführt...
          zeigeSpinner.value = false;
        });
    };

    async function versendeBuendelEmail() {
      const error = new Array<any>();
      const eingeschlosseneProdukte = FerienhausAnfrageService.getFHKProdukte(piniaStore.state);

      for await (const produktId of eingeschlosseneProdukte) {
        await FerienhausAnfrageService.versenden(
          piniaStore.getSessionToken(piniaStore.state),
          piniaStore.state,
          props.antrag,
          "VorschlagDirekt",
          produktId,
        )
          .then((res) => {
            Logger.debug("res ", res);
            Logger.debug(
              "submitModalData(): versenden erfolgreich. AnfrageID: " + res.data.id + " produktId: " + produktId,
            );
            piniaStore.state.id = res.data.id;
          })
          .catch((err) => {
            Logger.debug("ProduktId: ", produktId, " FEHLER in submitModalData():", err);
            error.push(err);
          });
      }

      if (error.length > 0) {
        showError.value = true;
      } else {
        showSuccess.value = true;
      }

      zeigeSpinner.value = false;
      emit("textVerbergen");
    }

    function versendeEmail() {
      AnfrageService.versenden(
        piniaStore.getSessionToken(piniaStore.state),
        piniaStore.state,
        props.antrag,
        "VorschlagDirekt",
      )
        .then((res) => {
          Logger.debug("res ", res);
          Logger.debug("submitModalData(): versenden erfolgreich. AnfrageID: " + res.data.id);
          piniaStore.state.id = res.data.id;
          showSuccess.value = true;
          emit("textVerbergen");
        })
        .catch((err) => {
          Logger.debug("FEHLER in submitModalData():", err);
          showError.value = true;
          zeigeSpinner.value = false;
          emit("textVerbergen");
        })
        .then(() => {
          //wird immer ausgeführt...
          zeigeSpinner.value = false;
        });
    }

    function resetForm() {
      showError.value = false;
      showSuccess.value = false;
    }

    function empfaengerVorbelegen() {
      const versicherungsnehmer = piniaStore.state.personendaten.versicherungsnehmer;
      state.value.anrede = versicherungsnehmer.anrede;
      state.value.vorname = versicherungsnehmer.vorname;
      state.value.nachname = versicherungsnehmer.nachname;
      state.value.email = versicherungsnehmer.email;
      state.value.telefon = versicherungsnehmer.telefon;
      state.value.werbeeinwilligung = piniaStore.state.werbeeinwilligung;
      state.value.datenschutzhinweiseAkzeptiert = piniaStore.state.datenschutzhinweiseAkzeptiert;
    }

    function setzeDatenschutzUndWerbeeinwilligung(event: Event) {
      const checked = (event.target as HTMLInputElement).checked;
      state.value.datenschutzhinweiseAkzeptiert = checked;
      state.value.werbeeinwilligung = checked.toString();
    }

    onMounted(() => {
      empfaengerVorbelegen();
    });

    return {
      headerText,
      submitModalData,
      closeModal,
      fehler,
      showError,
      showSuccess,
      resetForm,
      state,
      setzeDatenschutzUndWerbeeinwilligung,
    };
  },
});
</script>

<template>
  <form v-if="!showError && !showSuccess" id="vorschlagPerMailDirekt" novalidate>
    <Zeile>
      <titel-zelle :vertikal-zentrieren="false" :ausrichtung-responsiv="false">Anrede</titel-zelle>
      <zelle :breite="12" class="u-form-grid-nospace-y">
        <Anrede
          id="anrede"
          :required="isVorschlagEmail"
          v-model:modelValue="state.anrede"
          ariaRequired="true"
          ariaLabel="Anrede"
          tabindex="0"
          :autofocus="true"
          style="margin: 0 !important"
          :gemeinschaftsAnrede="false"
          :firmenAnrede="false"
        />
      </zelle>
    </Zeile>
    <Zeile>
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">Vorname</titel-zelle>
      <titel-zelle :breite="6" :vertikal-zentrieren="false" :ausrichtung-responsiv="false">Nachname</titel-zelle>
    </Zeile>
    <Zeile>
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          type="text"
          id="vorname"
          name="vorname"
          class="c-form-control__input"
          data-cy="vorname"
          placeholder="Vorname"
          minlength="2"
          maxlength="40"
          :required="isVorschlagEmail"
          :aria-required="isVorschlagEmail"
          aria-label="Vorname"
          data-fehlermeldung="Bitte geben Sie Ihren Vornamen ein (2-40 Zeichen)."
          v-model.lazy.trim="state.vorname"
        />
        <validierungs-fehler :fehler-text="fehler.vorname" />
      </zelle>
      <zelle :breite="6" class="u-form-grid-nospace-y">
        <input
          type="text"
          id="nachname"
          name="nachname"
          class="c-form-control__input"
          data-cy="nachname"
          placeholder="Name"
          minlength="2"
          maxlength="40"
          :required="isVorschlagEmail"
          :aria-required="isVorschlagEmail"
          aria-label="Nachname"
          data-parsley-combined-name-length="40"
          data-fehlermeldung="Bitte geben Sie Ihren Nachnamen ein (2-40 Zeichen)."
          v-model.lazy.trim="state.nachname"
        />
        <validierungs-fehler :fehler-text="fehler.nachname" />
      </zelle>
    </Zeile>
    <Zeile>
      <titel-zelle :vertikal-zentrieren="false" :ausrichtung-responsiv="false">E-Mail-Adresse</titel-zelle>
      <Zelle :breite="12" class="u-form-grid-nospace-y">
        <input
          type="email"
          id="email"
          name="email"
          class="c-form-control__input"
          data-cy="email"
          placeholder="E-Mail"
          minlength="2"
          maxlength="60"
          :required="isVorschlagEmail"
          :aria-required="isVorschlagEmail"
          aria-label="Email"
          data-fehlermeldung="Bitte geben Sie Ihre E-Mail-Adresse ein (2-60 Zeichen)."
          v-model.lazy.trim="state.email"
        />
        <validierungs-fehler :fehler-text="fehler.email" />
      </Zelle>
    </Zeile>
    <Zeile v-if="istOutboundAktiv">
      <titel-zelle :vertikal-zentrieren="false" :ausrichtung-responsiv="false">Telefon</titel-zelle>
      <Zelle :breite="12" class="u-form-grid-nospace-y">
        <input
          type="text"
          id="telefon"
          name="telefon"
          class="c-form-control__input"
          data-cy="telefon"
          placeholder="Telefon"
          minlength="6"
          maxlength="30"
          v-model.lazy.trim="state.telefon"
          aria-label="Telefon"
        />
        <validierungs-fehler :fehler-text="fehler.telefon" />
      </Zelle>
    </Zeile>
    <Zeile v-if="isVorschlagEmail" class="c-form-control-checkbox form-group">
      <Zelle :breite="12">
        <input
          id="datenschutzhinweiseAkzeptiert"
          v-model="state.datenschutzhinweiseAkzeptiert"
          :value="isVorschlagEmail"
          class="c-form-control-checkbox__input c-form-control-checkbox__input--spring"
          data-fehlermeldung="Bitte akzeptieren Sie die Datenschutzhinweise."
          :required="true"
          :aria-required="true"
          aria-label="Ja, die Datenschutzhinweise habe ich zur Kenntnis genommen"
          name="datenschutzhinweiseAkzeptiert"
          type="checkbox"
        />
        <label class="c-form-control-checkbox__label control-label" for="datenschutzhinweiseAkzeptiert">
          <icon-sprite :ist-klein="true" css-klassen="c-form-control-checkbox__icon" icon-name="icon-check-mark" />
          <span class="c-form-control-checkbox__label-text"
            >Ja, die
            <a class="agree-link" href="https://www.gev-versicherung.de/informationen/datenschutz/" target="_blank"
              >Datenschutzhinweise</a
            >
            habe ich zur Kenntnis genommen.
          </span>
        </label>
        <validierungs-fehler :fehler-text="fehler.datenschutzhinweiseAkzeptiert" />
      </Zelle>
    </Zeile>
    <Zeile v-else class="c-form-control-checkbox form-group">
      <Zelle :breite="12">
        <input
          id="datenschutzhinweiseAkzeptiert"
          @change="setzeDatenschutzUndWerbeeinwilligung"
          class="c-form-control-checkbox__input c-form-control-checkbox__input--spring"
          type="checkbox"
          :value="true"
          aria-label="Datenschutzhinweise habe ich zur Kenntnis genommen und bin damit einverstanden, dass
                die GEV meine Angaben im Onlineantrag vorübergehend speichert
                und mich in diesem Rahmen kontaktiert."
          data-fehlermeldung="Bitte akzeptieren Sie die Datenschutzhinweise."
          name="datenschutzhinweiseAkzeptiert"
        />
        <label class="c-form-control-checkbox__label control-label" for="datenschutzhinweiseAkzeptiert">
          <icon-sprite :ist-klein="true" css-klassen="c-form-control-checkbox__icon" icon-name="icon-check-mark" />
          <span class="c-form-control-checkbox__label-text"
            >Ja, die
            <a class="agree-link" href="https://www.gev-versicherung.de/informationen/datenschutz/" target="_blank"
              >Datenschutzhinweise</a
            >
            habe ich zur Kenntnis genommen und bin damit einverstanden, dass die GEV meine Angaben im Onlineantrag
            vorübergehend speichert und mich in diesem Rahmen kontaktiert. Die Daten werden entweder auf mein Verlangen
            oder spätestens nach 90 Tagen unwiderruflich gelöscht.
          </span>
        </label>
      </Zelle>
    </Zeile>
    <Zeile v-if="isVorschlagEmail && istOutboundAktiv" class="c-form-control-checkbox form-group">
      <Zelle :breite="12">
        <input
          id="werbeeinwilligung"
          v-model="state.werbeeinwilligung"
          class="c-form-control-checkbox__input c-form-control-checkbox__input--spring"
          type="checkbox"
          value="true"
          name="werbeeinwilligung"
          aria-label="Ja, ch bin damit einverstanden, dass die GEV meine Angaben im Onlineantrag vorübergehend speichert und
                mich in diesem Rahmen kontaktiert."
        />
        <label class="c-form-control-checkbox__label control-label" for="werbeeinwilligung">
          <icon-sprite :ist-klein="true" css-klassen="c-form-control-checkbox__icon" icon-name="icon-check-mark" />
          <span class="c-form-control-checkbox__label-text"
            >Ich bin damit einverstanden, dass die GEV meine Angaben im Onlineantrag vorübergehend speichert und mich in
            diesem Rahmen kontaktiert. Die Daten werden entweder auf mein Verlangen oder spätestens nach 90 Tagen
            unwiderruflich gelöscht.</span
          >
        </label>
        <validierungs-fehler :fehler-text="fehler.werbeeinwilligung" />
      </Zelle>
    </Zeile>
    <Zeile style="padding-top: 32px">
      <Zelle :breite="6" class="u-text-align-left">
        <button id="quoteByEmailCancelBtn" class="c-button c-button--inverted" type="button" @click="closeModal()">
          Abbrechen
        </button>
      </Zelle>
      <Zelle :breite="6" class="u-text-align-right">
        <button id="quoteByEmailContinueBtn" class="c-button right" type="button" @click="submitModalData()">
          {{ isVorschlagEmail === true ? "Per E-Mail anfordern" : "Vorschlag herunterladen" }}
        </button>
      </Zelle>
    </Zeile>
  </form>

  <Fehlermeldung v-if="showError" @closeModal="closeModal" @resetForm="resetForm" />

  <EmailBestaetigung v-if="showSuccess" @closeModal="closeModal" />
</template>
